import React, { useState, useEffect } from 'react';
import "../../css/AllClasses.css";
import Loader from "../../components/Loader";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaBook, FaSearch, FaTimes } from 'react-icons/fa';

function AllSubjects() {
    const [subjects, setSubjects] = useState([]);
    const [filteredSubjects, setFilteredSubjects] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchSubjects();
    }, []);

    const fetchSubjects = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            return;
        }

        setIsLoading(true);

        try {
            const response = await fetch("https://schoolpro-api.uniums.com/api/subjects", {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch subjects");
            }

            const data = await response.json();
            setSubjects(data);
            setFilteredSubjects(data);
        } catch (error) {
            console.error("Error fetching subjects:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle search
    useEffect(() => {
        let results = [...subjects];
        
        if (searchTerm.trim() !== '') {
            results = results.filter(subject =>
                (subject.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                (subject.code?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                (subject.department?.toLowerCase() || '').includes(searchTerm.toLowerCase())
            );
        }
        
        setFilteredSubjects(results);
    }, [searchTerm, subjects]);

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const clearSearch = () => {
        setSearchTerm('');
    };

    // Function to get category-specific classes for a subject card
    const getCategoryClass = (department) => {
        if (!department) return '';
        const dept = department.toLowerCase();
        if (dept.includes('science')) return 'allclasses-card-science';
        if (dept.includes('arts')) return 'allclasses-card-arts';
        if (dept.includes('commerce')) return 'allclasses-card-commerce';
        return '';
    };

    return (
        <div className="allclasses-container">
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} />

            {isLoading && <Loader />}

            <div className="allclasses-header">
                <div className="allclasses-title-container">
                    <FaBook className="allclasses-title-icon" />
                    <h1 className="allclasses-title">Subjects Overview</h1>
                </div>

                <div className="allclasses-stats">
                    <div className="allclasses-stat-box">
                        <div className="allclasses-stat-icon">
                            <FaBook />
                        </div>
                        <div className="allclasses-stat-info">
                            <span className="allclasses-stat-number">{subjects.length}</span>
                            <span className="allclasses-stat-label">Total Subjects</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="allclasses-filters">
                <div className="allclasses-search-container">
                    <FaSearch className="allclasses-search-icon" />
                    <input
                        type="text"
                        className="allclasses-search-input"
                        placeholder="Search subjects by name, code, or department..."
                        value={searchTerm}
                        onChange={handleSearch}
                    />
                    {searchTerm && (
                        <button className="allclasses-search-clear" onClick={clearSearch}>
                            <FaTimes />
                        </button>
                    )}
                </div>
            </div>

            <div className="allclasses-list-container">
                {filteredSubjects.length === 0 ? (
                    <div className="allclasses-empty-state">
                        <FaBook className="allclasses-empty-icon" />
                        <h3>No Subjects Found</h3>
                        {searchTerm ? (
                            <p>No subjects match your search criteria.</p>
                        ) : (
                            <p>There are no subjects available in the system.</p>
                        )}
                        {searchTerm && (
                            <button className="allclasses-btn allclasses-btn-primary" onClick={clearSearch}>
                                Clear Search
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="allclasses-grid">
                        {filteredSubjects.map((subject) => {
                            const categoryClass = getCategoryClass(subject.department);
                            
                            return (
                                <div 
                                    key={subject._id} 
                                    className={`allclasses-card ${categoryClass}`}
                                >
                                    <div className="allclasses-card-header">
                                        <div className="allclasses-card-badge">{subject.code}</div>
                                        <h3 className="allclasses-card-title">
                                            {subject.name}
                                        </h3>
                                    </div>
                                    <div className="allclasses-card-body">
                                        {subject.department && (
                                            <div className="allclasses-card-detail">
                                                <span className="allclasses-card-label">Department:</span>
                                                <span className="allclasses-card-value">
                                                    {subject.department}
                                                </span>
                                            </div>
                                        )}
                                        {subject.credits && (
                                            <div className="allclasses-card-detail">
                                                <span className="allclasses-card-label">Credits:</span>
                                                <span className="allclasses-card-value">
                                                    {subject.credits}
                                                </span>
                                            </div>
                                        )}
                                        {subject.description && (
                                            <div className="allclasses-card-detail">
                                                <span className="allclasses-card-label">Description:</span>
                                                <span className="allclasses-card-value">
                                                    {subject.description}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

export default AllSubjects;